import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';

import CheckoutForm from "./views/CheckoutFormView";
import ThankYouPage from "./views/ThankYouPageView";
import CheckOrder from "./views/CheckOrderView";
import { checkPaymentCallback } from './supabaseClient';

import './App.tailwind.css';

const safeExternalUrl = (value) => {
  try {
    const url = new URL(value);
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return url.toString();
    }
  } catch (error) {
    console.error('Invalid redirect URL:', error);
  }
  return null;
};

// Redirect component for automatic redirection
const RedirectToCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // First check if this is a payment callback
    const paymentResult = checkPaymentCallback();
    
    if (paymentResult.success) {
      // This was a payment callback, redirect to thank-you page
      if (paymentResult.status === 'completed') {
        navigate('/thank-you');
        return;
      }
    }
    
    // If not a callback, check for cart data
    const cartData = localStorage.getItem('checkout_cart_data');
    
    if (cartData) {
      const parsedData = JSON.parse(cartData);
      if (parsedData.items && parsedData.items.length > 0) {
        console.log('Found existing cart data, redirecting to checkout...');
        // Redirect to checkout if cart has items
        navigate('/checkout');
        return;
      }
    }
    
    // If we get here, cart is empty or doesn't exist - immediately redirect to external site
    console.log('Cart is empty, redirecting to drivi.pro...');
    window.location.href = 'https://drivi.pro';
  }, [navigate, location]);
  
  // Return loading state while redirection happens
  return <div className="loading-spinner">Processing...</div>;
};

// Payment callback handler
const PaymentCallback = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    const paymentResult = checkPaymentCallback();
    
    if (paymentResult.success) {
      if (paymentResult.status === 'completed') {
        navigate('/thank-you');
      } else {
        navigate('/checkout');
      }
    } else {
      navigate('/checkout');
    }
  }, [navigate]);
  
  return <div className="loading-spinner">Processing payment...</div>;
};

// Catch-all route redirects to drivi.pro
const RedirectToDriviPro = () => {
  useEffect(() => {
    window.location.href = 'https://drivi.pro';
  }, []);
  
  return <div className="loading-spinner">Redirecting...</div>;
};

// smart-hide redirect hop #1
const SmartHideStepOne = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const next = params.get('next');
    const safeNext = safeExternalUrl(next);
    if (!safeNext) {
      window.location.href = '/checkout';
      return;
    }
    window.location.replace(safeNext);
  }, [location.search]);

  return <div className="loading-spinner">Redirecting...</div>;
};

// smart-hide redirect hop #2
const SmartHideStepTwo = () => {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const target = params.get('target');
    const safeTarget = safeExternalUrl(target);
    if (!safeTarget) {
      window.location.href = '/checkout';
      return;
    }
    window.location.replace(safeTarget);
  }, [location.search]);

  return <div className="loading-spinner">Redirecting to payment...</div>;
};

const App = () => {
  // Initialize Kit.com autofill functionality
  React.useEffect(() => {
    // Check if we're on Kit.com and execute the script loader
    if (window.location.hostname.includes('kit.com')) {
      console.log('Kit.com detected - initializing autofill');
      const scriptLoader = localStorage.getItem('kit_script_loader');
      if (scriptLoader) {
        try {
          // Execute the script loader
          eval(scriptLoader);
        } catch (e) {
          console.error('Error executing Kit.com autofill script:', e);
        }
      }
    }
  }, []);

  return (
    <Router>
      <div className="app">
        <div className="checkout-container">
          <Routes>
            <Route path="/" element={<RedirectToCheckout />} />
            <Route path="/checkout" element={<CheckoutForm />} />
            <Route path="/smart-hide/step-1" element={<SmartHideStepOne />} />
            <Route path="/smart-hide/step-2" element={<SmartHideStepTwo />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/order/:paymentIntentId" element={<CheckOrder />} />
            <Route path="/payment/callback" element={<PaymentCallback />} />
            {/* Catch-all route redirects to drivi.pro */}
            <Route path="*" element={<RedirectToDriviPro />} />
          </Routes>
        </div>
      </div>
    </Router>
  );
};

export default App;
